import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Typography,
  Box,
  Button,
  Alert,
  Snackbar,
  Grid,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import api from '../services/api';
import schedulesService from '../services/schedulesService';
import placeServicesService from '../services/placeServicesService';
import placesService from '../services/placesService';
import useCRUD from '../hooks/useCRUD';
import useDialog from '../hooks/useDialog';
import useSnackbar from '../hooks/useSnackbar';
import { useContextualHint } from '../hooks/useContextualHint';
import LoadingState from '../components/ui/LoadingState';
import EmptyState from '../components/ui/EmptyState';
import ErrorState from '../components/ui/ErrorState';
import ContextualHint from '../components/ContextualHint';
import DeleteConfirmDialog from '../components/ui/DeleteConfirmDialog';
import ScheduleForm from '../components/forms/ScheduleForm';
import ScheduleFilters from '../components/ScheduleFilters';
import ScheduleCard from '../components/ScheduleCard';
import ShareScheduleDialog from '../components/ShareScheduleDialog';
import ShiftForm from '../components/ShiftForm';
import { trackEvent, trackError, trackFormAbandonment } from '../utils/tracking';

export default function SchedulesPage() {
  console.log('[SchedulesPage] Componente renderizado - timestamp:', Date.now());
  
  // ✅ Custom Hooks
  const { items: schedules, loading, error, reload, create, update, deleteItem } = useCRUD(schedulesService);
  const formDialog = useDialog();
  const deleteDialog = useDialog();
  const shiftDialog = useDialog();
  const shareDialog = useDialog();
  const { snackbar, showSuccess, showError, showWarning, hideSnackbar } = useSnackbar();

  // Estados adicionais específicos de schedules
  const [professionals, setProfessionals] = useState([]);
  const [places, setPlaces] = useState([]);
  const [placeProfessionals, setPlaceProfessionals] = useState([]);
  const [ScheduleShifts, setAgendaShifts] = useState({});
  const [availablePlaceServices, setAvailablePlaceServices] = useState([]);
  const [shareUrl, setShareUrl] = useState('');
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProfessional, setFilterProfessional] = useState('');
  const [filterPlace, setFilterPlace] = useState('');

  // Obter dica contextual
  const hint = useContextualHint('schedules', { items: schedules || [], shifts: ScheduleShifts });

  // Carregar dados complementares (apenas uma vez na montagem)
  useEffect(() => {
    console.log('[SchedulesPage] useEffect de loadNormalizedData disparado');
    
    const loadNormalizedData = async () => {
      console.log('[SchedulesPage] Iniciando loadNormalizedData...');
      try {
        // Buscar dados normalizados da API
        console.log('[SchedulesPage] Chamando schedulesService.getAllWithRelations()');
        const normalizedData = await schedulesService.getAllWithRelations();
        console.log('[SchedulesPage] Dados recebidos:', {
          schedules: normalizedData.schedules?.length,
          professionals: normalizedData.professionals?.length,
          places: normalizedData.places?.length,
          placeProfessionals: normalizedData.placeProfessionals?.length
        });
        
        setProfessionals(normalizedData.professionals || []);
        setPlaces(normalizedData.places || []);
        setPlaceProfessionals(normalizedData.placeProfessionals || []);
        
        // Carregar turnos se houver schedules
        if (normalizedData.schedules?.length > 0) {
          console.log('[SchedulesPage] Carregando turnos para', normalizedData.schedules.length, 'schedules');
          loadShiftsForAgendas(normalizedData.schedules);
        }
      } catch (error) {
        console.error('[SchedulesPage] Erro ao carregar dados normalizados:', error);
        setProfessionals([]);
        setPlaces([]);
        setPlaceProfessionals([]);
      }
    };
    
    loadNormalizedData();
  }, []); // Apenas na montagem do componente

  const loadAllPlaceProfessionals = async (placesData, SchedulesData, professionalsData) => {
    console.log('[SchedulesPage] Criando placeProfessionals a partir de', placesData.length, 'locais');
    try {
      const allPlaceProfessionals = [];
      
      // Criar placeProfessionals a partir dos professionalIds de cada place
      placesData.forEach(place => {
        if (place.professionalIds && place.professionalIds.length > 0) {
          place.professionalIds.forEach(profId => {
            const professional = professionalsData.find(p => p.id === profId);
            if (professional) {
              // ✅ NORMALIZADO: schedule agora tem placeId e professionalId diretamente
              const existingAgenda = SchedulesData.find(
                a => a.placeId === place.id && a.professionalId === profId
              );
              
              allPlaceProfessionals.push({
                id: existingAgenda?.placeProfessionalId || `${place.id}-${profId}`, // ID real ou temporário
                placeId: place.id,
                placeName: place.name,
                professionalId: profId,
                professionalName: professional.name,
                isActive: existingAgenda?.isActive ?? true
              });
            }
          });
        }
      });
      
      console.log('[SchedulesPage] Total de placeProfessionals criados:', allPlaceProfessionals.length);
      setPlaceProfessionals(allPlaceProfessionals);
    } catch (error) {
      console.error('[SchedulesPage] Erro ao criar placeProfessionals:', error);
      setPlaceProfessionals([]);
    }
  };

  const loadShiftsForAgendas = async (SchedulesData) => {
    try {
      const shiftsMap = {};
      await Promise.all(
        SchedulesData.map(async (schedule) => {
          try {
            const shifts = await schedulesService.getShifts(schedule.id);
            shiftsMap[schedule.id] = shifts;
          } catch (error) {
            console.error(`Erro ao carregar turnos da schedule ${schedule.id}:`, error);
            shiftsMap[schedule.id] = [];
          }
        })
      );
      setAgendaShifts(shiftsMap);
    } catch (error) {
      console.error('Erro ao carregar turnos:', error);
    }
  };

  // ✅ Handlers de CRUD
  // ✅ Callbacks memoizados para cards
  const handleEditAgenda = useCallback((schedule) => {
    formDialog.handleOpen(schedule);
  }, [formDialog]);

  const handleDeleteAgenda = useCallback((schedule) => {
    deleteDialog.handleOpen(schedule);
  }, [deleteDialog]);

  const handleShareAgenda = useCallback((schedule) => {
    const shifts = ScheduleShifts[schedule.id] || [];
    
    if (shifts.length === 0) {
      showWarning('Esta schedule precisa ter turnos e horários configurados para poder ser compartilhada.');
      return;
    }
    
    const url = `https://share.agendario.com.br/schedule/${schedule.id}`;
    setShareUrl(url);
    shareDialog.handleOpen(schedule);
  }, [ScheduleShifts, showWarning, shareDialog]);

  const handleCreate = async (data) => {
    try {
      const result = await create(data);
      formDialog.handleClose();
      
      const professionalName = professionals.find(p => p.id === data.professionalId)?.name;
      const placeName = places.find(p => p.id === data.placeId)?.name;
      
      trackEvent('agenda_created', {
        agenda_id: result.id || 'unknown',
        professional_name: professionalName,
        place_name: placeName,
        max_booking_days_ahead: data.maxBookingDaysAhead,
        max_available_days_to_show: data.maxAvailableDaysToShow
      });
      
      showSuccess('schedule criada com sucesso!');
      await loadShiftsForAgendas(schedules);
    } catch (err) {
      trackError('agenda_save_error', err, {
        is_update: false,
        professional_id: data.professionalId,
        place_id: data.placeId
      });
      showError('Erro ao criar schedule: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleUpdate = async (data) => {
    try {
      await update(formDialog.data.id, data);
      formDialog.handleClose();
      
      const professionalName = professionals.find(p => p.id === data.professionalId)?.name;
      const placeName = places.find(p => p.id === data.placeId)?.name;
      
      trackEvent('agenda_updated', {
        agenda_id: formDialog.data.id,
        professional_name: professionalName,
        place_name: placeName,
        max_booking_days_ahead: data.maxBookingDaysAhead
      });
      
      showSuccess('schedule atualizada com sucesso!');
    } catch (err) {
      trackError('agenda_save_error', err, {
        is_update: true,
        agenda_id: formDialog.data.id
      });
      showError('Erro ao atualizar schedule: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSubmit = formDialog.isEdit ? handleUpdate : handleCreate;

  const handleDelete = async () => {
    console.log('[SchedulesPage] handleDelete INICIADO');
    console.log('[SchedulesPage] deleteDialog.data:', deleteDialog.data);
    console.log('[SchedulesPage] deleteItem type:', typeof deleteItem);
    console.log('[SchedulesPage] deleteItem:', deleteItem);
    
    try {
      console.log('[SchedulesPage] Tentando excluir schedule ID:', deleteDialog.data?.id);
      const ScheduleName = deleteDialog.data?.name;
      const ScheduleId = deleteDialog.data?.id;
      console.log('[SchedulesPage] Nome da schedule:', ScheduleName);
      console.log('[SchedulesPage] ID da schedule:', ScheduleId);
      
      if (typeof deleteItem !== 'function') {
        console.error('[SchedulesPage] deleteItem NÃO É UMA FUNÇÃO!');
        throw new Error('deleteItem não é uma função válida');
      }
      
      console.log('[SchedulesPage] Chamando deleteItem com ID:', ScheduleId);
      const result = await deleteItem(ScheduleId);
      console.log('[SchedulesPage] deleteItem concluído com sucesso. Result:', result);
      
      console.log('[SchedulesPage] Fechando dialog...');
      deleteDialog.handleClose();
      console.log('[SchedulesPage] Dialog fechado');
      
      console.log('[SchedulesPage] Tracking evento...');
      trackEvent('agenda_deleted', {
        agenda_id: deleteDialog.data.id,
        agenda_name: ScheduleName
      });
      
      console.log('[SchedulesPage] Mostrando mensagem de sucesso...');
      showSuccess('schedule excluída com sucesso!');
      console.log('[SchedulesPage] handleDelete FINALIZADO COM SUCESSO');
    } catch (err) {
      console.error('[SchedulesPage] ERRO em handleDelete:', err);
      console.error('[SchedulesPage] Erro stack:', err.stack);
      console.error('[SchedulesPage] Erro response:', err.response);
      
      trackError('agenda_delete_error', err, {
        agenda_id: deleteDialog.data?.id
      });
      showError('Erro ao excluir schedule: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      showSuccess('Link copiado com sucesso!');
    } catch (error) {
      showError('Erro ao copiar link');
    }
  };

  const handleOpenUrl = () => {
    window.open(shareUrl, '_blank');
  };

  // ✅ Handlers de Turnos (Shifts)
  const handleOpenShift = async (schedule, shift = null) => {
    try {
      // ✅ NORMALIZADO: schedule agora tem placeId e professionalId diretamente
      const placeId = schedule.placeId;
      const professionalId = schedule.professionalId;
      const placeProfessionalId = schedule.placeProfessionalId;
      
      if (!placeId) {
        showError('Erro: Local não identificado na schedule');
        return;
      }
      
      if (!professionalId) {
        showError('Erro: Profissional não identificado na schedule');
        return;
      }
      
      // Buscar TODOS os serviços do local
      const allPlaceServices = await placeServicesService.getByPlaceId(placeId);
      
      // Buscar os serviços que o profissional pode realizar
      // Fazemos isso percorrendo cada placeService e buscando se o profissional está vinculado
      const professionalPlaceServices = [];
      
      for (const placeService of allPlaceServices) {
        try {
          // IMPORTANTE: usar placeService.id (placeServiceId), não serviceId
          // A rota é: /places/{placeId}/services/{placeServiceId}/professionals
          const professionals = await api.get(`/places/${placeId}/services/${placeService.id}/professionals`);
          const professionalsData = professionals.data?.$values || professionals.data || [];
          
          // Verificar se o profissional da schedule está nessa lista
          const isProfessionalLinked = professionalsData.some(
            p => p.professionalId === professionalId || p.placeProfessionalId === placeProfessionalId
          );
          
          if (isProfessionalLinked) {
            professionalPlaceServices.push(placeService);
          }
        } catch (error) {
          console.warn(`Erro ao verificar profissionais do placeService ${placeService.id}:`, error);
          // Continua para próximo serviço
        }
      }
      
      console.log('[SchedulesPage] Serviços filtrados para profissional:', {
        total: allPlaceServices.length,
        filtrados: professionalPlaceServices.length,
        professionalId,
        placeId
      });
      
      setAvailablePlaceServices(professionalPlaceServices);
      shiftDialog.handleOpen({ ...shift, ScheduleId: schedule.id, schedule });
    } catch (error) {
      console.error('Erro ao carregar serviços do local:', error);
      setAvailablePlaceServices([]);
      showError('Erro ao carregar serviços oferecidos no local. Verifique se o local possui serviços configurados.');
    }
  };

  const handleShiftSubmit = async (shiftData) => {
    try {
      const ScheduleId = shiftDialog.data.ScheduleId;
      const shiftId = shiftDialog.data.id;
      
      if (shiftId) {
        await schedulesService.updateShift(ScheduleId, shiftId, shiftData);
        showSuccess('Turno atualizado com sucesso!');
      } else {
        await schedulesService.createShift(ScheduleId, shiftData);
        showSuccess('Turno criado com sucesso!');
      }
      
      shiftDialog.handleClose();
      await loadShiftsForAgendas(schedules);
    } catch (error) {
      showError('Erro ao salvar turno: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleShiftDelete = async (shiftId) => {
    try {
      const ScheduleId = shiftDialog.data.ScheduleId;
      await schedulesService.deleteShift(ScheduleId, shiftId);
      showSuccess('Turno excluído com sucesso!');
      shiftDialog.handleClose();
      await loadShiftsForAgendas(schedules);
    } catch (error) {
      showError('Erro ao excluir turno: ' + (error.response?.data?.message || error.message));
    }
  };

  // ✅ Helpers
  const getProfessionalName = (professionalId) => {
    return professionals.find(p => p.id === professionalId)?.name || 'N/A';
  };

  const getPlaceName = (placeId) => {
    return places.find(p => p.id === placeId)?.name || 'N/A';
  };

  const formatShiftDays = (shift) => {
    const days = [];
    if (shift.monday) days.push('Seg');
    if (shift.tuesday) days.push('Ter');
    if (shift.wednesday) days.push('Qua');
    if (shift.thursday) days.push('Qui');
    if (shift.friday) days.push('Sex');
    if (shift.saturday) days.push('Sáb');
    if (shift.sunday) days.push('Dom');
    return days.join(', ');
  };

  const formatTime = (timeSpan) => {
    if (!timeSpan) return '';
    if (typeof timeSpan === 'string') {
      return timeSpan.substring(0, 5);
    }
    return timeSpan;
  };

  // ✅ Filtros memoizados
  const filteredAgendas = useMemo(() => {
    return schedules.filter((schedule) => {
      const matchesSearchTerm = !searchTerm || 
        schedule.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        schedule.description?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // ✅ NORMALIZADO: schedule tem professionalId e placeId diretamente
      const matchesProfessional = !filterProfessional || 
        schedule.professionalId === filterProfessional;
      
      const matchesPlace = !filterPlace || 
        schedule.placeId === filterPlace;
    
      return matchesSearchTerm && matchesProfessional && matchesPlace;
    });
  }, [schedules, searchTerm, filterProfessional, filterPlace]);

  const clearFilters = () => {
    setSearchTerm('');
    setFilterProfessional('');
    setFilterPlace('');
  };

  // ✅ Loading/Error States
  if (loading) return <LoadingState message="Carregando schedules..." />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          schedules
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => formDialog.handleOpen(null)}
        >
          Nova schedule
        </Button>
      </Box>

      {/* Dica Contextual */}
      <ContextualHint hint={hint} onActionClick={() => formDialog.handleOpen(null)} />

      {/* Filtros */}
      <ScheduleFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        filterProfessional={filterProfessional}
        onProfessionalChange={setFilterProfessional}
        filterPlace={filterPlace}
        onPlaceChange={setFilterPlace}
        professionals={professionals}
        places={places}
        onClearFilters={clearFilters}
      />

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Configure schedules automáticas vinculando profissionais a locais.
          A geração automática cria horários disponíveis com base nos turnos configurados.
        </Typography>
      </Alert>

      {/* Empty State */}
      {filteredAgendas.length === 0 ? (
        <EmptyState
          icon={() => <Typography fontSize={64}>📅</Typography>}
          title={schedules.length === 0 ? 'Nenhuma schedule cadastrada' : 'Nenhuma schedule encontrada'}
          message={
            schedules.length === 0 
              ? 'Clique em "Nova schedule" para começar.' 
              : 'Tente ajustar os filtros para encontrar suas schedules.'
          }
          action={
            schedules.length === 0 && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => formDialog.handleOpen(null)}>
                Nova schedule
              </Button>
            )
          }
        />
      ) : (
        <Grid container spacing={3}>
          {filteredAgendas.map((schedule) => (
            <Grid item xs={12} sm={6} md={4} key={schedule.id}>
              <ScheduleCard
                schedule={schedule}
                professionalName={getProfessionalName(schedule.professionalId)}
                placeName={getPlaceName(schedule.placeId)}
                shifts={ScheduleShifts[schedule.id] || []}
                onEdit={handleEditAgenda}
                onDelete={handleDeleteAgenda}
                onShare={handleShareAgenda}
                onAddShift={() => handleOpenShift(schedule)}
                onEditShift={(shift) => handleOpenShift(schedule, shift)}
                formatShiftDays={formatShiftDays}
                formatTime={formatTime}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* schedule Form Dialog */}
      <ScheduleForm
        open={formDialog.open}
        onClose={formDialog.handleClose}
        onSubmit={handleSubmit}
        data={formDialog.data}
        professionals={professionals}
        places={places}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialog.open}
        onClose={deleteDialog.handleClose}
        onConfirm={handleDelete}
        title="Confirmar Exclusão de Agenda"
        itemName={deleteDialog.data?.name}
        itemType="agenda"
        severity="error"
        impacts={[
          'Todos os turnos configurados serão removidos',
          'Todos os horários (time slots) serão excluídos',
          'Agendamentos futuros vinculados a esta agenda serão afetados',
          'O link de compartilhamento público deixará de funcionar',
          'Configurações de serviços e disponibilidade serão perdidas'
        ]}
      />

      {/* Share Dialog */}
      <ShareScheduleDialog
        open={shareDialog.open}
        onClose={shareDialog.handleClose}
        shareUrl={shareUrl}
        onCopy={handleCopyUrl}
        onOpen={handleOpenUrl}
      />

      {/* Shift Form Dialog */}
      <ShiftForm
        open={shiftDialog.open}
        onClose={shiftDialog.handleClose}
        onSubmit={handleShiftSubmit}
        onDelete={handleShiftDelete}
        shift={shiftDialog.data}
        ScheduleId={shiftDialog.data?.ScheduleId}
        availablePlaceServices={availablePlaceServices}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
